import type { ScopedThreadRef, ThreadId } from "@cmmd-forge/contracts";
import { newCommandId } from "../lib/utils";

import { selectThreadByRef } from "../store";
import type { SidebarThreadSummary } from "../types";

type LiveThread = NonNullable<ReturnType<typeof selectThreadByRef>>;

export type ResolvedThreadTarget = {
  readonly thread: LiveThread;
  readonly threadRef: ScopedThreadRef;
};

function snapshotsMatchTarget(
  target: ScopedThreadRef,
  snapshot: Pick<SidebarThreadSummary, "environmentId" | "id">,
): boolean {
  return snapshot.id === target.threadId && snapshot.environmentId === target.environmentId;
}

export function resolveThreadOrThrow<
  TSnapshot extends Pick<SidebarThreadSummary, "environmentId" | "id">,
>(
  target: ScopedThreadRef,
  resolved: ResolvedThreadTarget | null,
  snapshot: TSnapshot | undefined,
  mismatchMessage: string,
  unavailableMessage: string,
): LiveThread | TSnapshot {
  if (!resolved && snapshot && !snapshotsMatchTarget(target, snapshot)) {
    throw new Error(mismatchMessage);
  }
  const thread = resolved?.thread ?? snapshot;
  if (!thread) throw new Error(unavailableMessage);
  return thread;
}

export function resolveThreadOrNull<
  TSnapshot extends Pick<SidebarThreadSummary, "environmentId" | "id">,
>(
  target: ScopedThreadRef,
  resolved: ResolvedThreadTarget | null,
  snapshot: TSnapshot | undefined,
  mismatchMessage: string,
): LiveThread | TSnapshot | null {
  if (!resolved && snapshot && !snapshotsMatchTarget(target, snapshot)) {
    throw new Error(mismatchMessage);
  }
  return resolved?.thread ?? snapshot ?? null;
}

export function resolveArchiveDetails(
  thread: Pick<SidebarThreadSummary, "archive" | "archivedAt">,
  snapshot: Pick<SidebarThreadSummary, "archive" | "archivedAt"> | undefined,
) {
  const source = snapshot ?? thread;
  return {
    archive: source.archive ?? null,
    archivedAt: source.archivedAt,
  };
}

export function canArchiveThread(
  thread: Pick<SidebarThreadSummary, "session">,
  archivedAt: string | null,
  archiveState: string | null,
) {
  return (
    !(thread.session?.status === "running" && thread.session.activeTurnId != null) &&
    archivedAt === null &&
    (archiveState === null || archiveState === "failed")
  );
}

export function resolveArchiveAction(archiveState: string | null): "request" | "retry" {
  return archiveState === "failed" ? "retry" : "request";
}

function resolveUnarchiveAction(archiveState: string | null): "cancel" | "restore" {
  return archiveState === "requested" || archiveState === "failed" ? "cancel" : "restore";
}

/**
 * Picks the restore or cancel action, and for a restore the runtime that has
 * to carry it out.
 *
 * A restore has to reach the runtime. CMMD records the owner's choice, but
 * `archivedAt` only ever moves through the decider, and the archive
 * reconciler has no restore counterpart to dispatch the command later: it
 * claims Requested operations and dispatches `thread.archive` alone. Reading
 * the runtime before the CMMD write means an unreachable runtime fails here
 * rather than leaving CMMD restored and the thread still archived everywhere
 * it is read.
 *
 * A cancel is different. The archive never reached `complete`, so `archivedAt`
 * was never stamped and the decider would refuse the command. Cancelling stays
 * a CMMD-only write and works while the runtime is offline, which is most of
 * why someone cancels.
 */
export function resolveUnarchiveDispatch<TRuntimeApi>(
  archiveState: string | null,
  readRuntimeApi: () => TRuntimeApi | undefined,
): {
  readonly action: "cancel" | "restore";
  readonly runtimeApi: TRuntimeApi | null;
} {
  const action = resolveUnarchiveAction(archiveState);
  if (action === "cancel") return { action, runtimeApi: null };
  const runtimeApi = readRuntimeApi();
  if (!runtimeApi) throw new Error("Thread is unavailable for archive restoration.");
  return { action, runtimeApi };
}

/**
 * Only the `thread.unarchive` command clears `archivedAt` on the Forge
 * projection, so a restore that stopped at the CMMD write would leave the
 * thread archived. A cancel has no runtime api and dispatches nothing.
 */
export async function dispatchUnarchiveCommand(
  runtimeApi: {
    readonly orchestration: {
      readonly dispatchCommand: (command: {
        readonly type: "thread.unarchive";
        readonly commandId: ReturnType<typeof newCommandId>;
        readonly threadId: ThreadId;
      }) => Promise<unknown>;
    };
  } | null,
  threadId: ThreadId,
): Promise<void> {
  await runtimeApi?.orchestration.dispatchCommand({
    type: "thread.unarchive",
    commandId: newCommandId(),
    threadId,
  });
}
